import type { ReactElement } from 'react';

import heroPhoto from '@/shared/assets/education/hero/main-bg.jpg';

/**
 * 교육 히어로 카드 안의 그림. 전부 「무엇을 배우는지」를 한 장면으로 보여준다 —
 * 도형 장식이 아니라 수업에서 실제로 보게 될 화면(대화창·도구·자동화 흐름)이다.
 * 색은 토큰만 쓴다(민트·잉크·흰색).
 */

const INK = '#0B0F0D';

/** 01 AI 이해 — 질문 하나에 AI 가 답을 이어 쓰는 대화창 */
export function ChatArt(): ReactElement {
  return (
    <svg
      viewBox="0 0 200 150"
      aria-hidden="true"
      className="absolute right-[6%] bottom-[7%] w-[48%] drop-shadow-[0_14px_20px_rgb(6_17_11/0.22)] sm:w-[62%]"
    >
      <rect x="72" y="6" width="122" height="36" rx="18" fill="#FFFFFF" />
      <rect x="88" y="20" width="74" height="8" rx="4" fill={INK} opacity="0.22" />
      <rect x="6" y="52" width="170" height="92" rx="22" fill={INK} />
      {/* 4갈래 반짝임 — 흔히 쓰는 「AI 가 쓰는 중」 표지 */}
      <path
        d="M30 70l3.5 8.5L42 82l-8.5 3.5L30 94l-3.5-8.5L18 82l8.5-3.5z"
        className="fill-primary"
      />
      <rect x="52" y="72" width="102" height="8" rx="4" className="fill-primary" />
      <rect x="52" y="90" width="110" height="8" rx="4" fill="#FFFFFF" opacity="0.5" />
      <rect x="52" y="108" width="72" height="8" rx="4" fill="#FFFFFF" opacity="0.32" />
      <rect x="130" y="106" width="3" height="13" rx="1.5" className="fill-primary" />
    </svg>
  );
}

/**
 * 02 현장 실습 — 실제 강의 사진을 아치로 오리고, 수업에서 다루는 도구 이름을 얹는다.
 * 사진은 이 페이지에서 읽는 사람이 검증할 수 있는 유일한 증거라 장식으로 숨기지 않는다.
 * 도구 목록은 FAQ 가 말하는 것과 같다. 칩은 글자라 숨기지 않는다 — 보조기기도 도구 이름을 읽는다.
 */
export function PracticeArt(): ReactElement {
  return (
    <>
      {/* 아치와 칩은 설명 두 줄 아래에서 시작한다 — 위로 올리면 칩이 문장을 가린다 */}
      <div className="absolute inset-x-[9%] top-[58%] bottom-0 overflow-hidden rounded-t-full lg:top-[55%]">
        <img
          src={heroPhoto}
          alt="오톱 교육 현장 — 수강생들이 노트북으로 실습하는 모습"
          loading="eager"
          className="size-full object-cover object-[70%_32%]"
        />
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-[52%] lg:top-[49%]">
        <span className="-rotate-6 absolute left-[8%] rounded-full bg-background px-3 py-1 text-[11.5px] font-semibold text-foreground shadow-md">
          ChatGPT
        </span>
        <span className="absolute left-[40%] rotate-3 rounded-full bg-primary px-3 py-1 text-[11.5px] font-semibold text-on-mint shadow-md">
          Claude
        </span>
        <span className="-rotate-2 absolute right-[7%] rounded-full bg-mint-soft px-3 py-1 text-[11.5px] font-semibold text-on-mint shadow-md">
          Gemini
        </span>
      </div>
    </>
  );
}

const FLOW = [
  { label: '메일 수집', indent: 'ml-0' },
  { label: 'AI 요약', indent: 'ml-[12%]' },
] as const;

/** 03 업무 자동화 — 반복 업무가 사람 손을 거치지 않고 끝까지 흘러가는 모습 */
export function FlowArt(): ReactElement {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-[7%] bottom-[7%] flex flex-col gap-1.5 text-[12px] font-semibold"
    >
      {FLOW.map((step, index) => (
        <span
          key={step.label}
          className={`${step.indent} flex w-fit items-center gap-2 rounded-xl bg-background px-3 py-2 text-foreground shadow-[0_8px_16px_-10px_rgb(6_17_11/0.4)]`}
        >
          <span className="grid size-5 place-items-center rounded-full bg-mint-soft font-mono text-[10.5px]">
            {index + 1}
          </span>
          {step.label}
        </span>
      ))}
      <span className="ml-[24%] flex w-fit items-center gap-2 rounded-xl bg-ink-surface px-3 py-2 text-on-ink shadow-[0_8px_16px_-10px_rgb(6_17_11/0.5)]">
        <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
          <circle cx="10" cy="10" r="10" className="fill-primary" />
          <path
            d="M6 10.2l2.6 2.6L14 7.4"
            fill="none"
            stroke={INK}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        보고서 완성
      </span>
    </div>
  );
}
